/**
 * Money — renders a currency amount with the Saudi Riyal symbol ALWAYS to the LEFT
 * of the digits, in both LTR and RTL UIs (per brand guideline). Wrapped in dir="ltr"
 * so the symbol-then-digits order is preserved even on Arabic pages.
 *
 * Usage: <Money>{value}</Money>  or  <Money value={a} />
 *
 * `value` exists because its absence failed SILENTLY. A `<Money value={x} />`
 * used to render the Riyal glyph and nothing else — no warning, no crash, just
 * a price with no digits — and that shipped to production in PartDetailsModal,
 * where every figure on the part details screen rendered as a bare symbol.
 * A primitive whose misuse is invisible is a primitive that will be misused
 * again, so it now accepts both forms. Children still win when both are given.
 *
 * ── GROUPING (owner, 2026-09-07) ─────────────────────────────────────────────
 * Every amount in the app reads 000,000,000.00. Reported against Fixed Assets,
 * which showed a cost of `100000.00` — six digits an accountant has to count
 * with a finger to know whether it is a hundred thousand or a million.
 *
 * It is done HERE, in the one component every amount already flows through,
 * rather than at ~360 call sites. Fixing the callers would have missed the ones
 * nobody grepped and would go stale the next time somebody renders a total.
 *
 * The format is FIXED, never locale-derived: en-US grouping, Western digits,
 * comma thousands, dot decimal — on the Arabic UI too. A locale-aware
 * formatter under `ar` produces Eastern-Arabic digits (١٠٠٬٠٠٠) and an Arabic
 * thousands mark, which is not what was asked for and does not match the Riyal
 * glyph sitting beside it. This mirrors the rule the date helpers already
 * follow, where a bare 'ar-SA' silently switches calendars.
 *
 * ── WHY THIS IS SAFE FOR ALL 337 `children` CALLERS ──────────────────────────
 * `children` is formatted only when it is unambiguously a plain number: a
 * number, or a string holding nothing but an optional sign, digits and one dot.
 * Everything else is passed through untouched, which leaves alone:
 *
 *   - already-grouped strings ("1,234.00") — a comma means somebody formatted
 *     it already, and re-parsing would read 1,234 as 1;
 *   - ranges and compound labels ("10 - 20", "12.00 / hr");
 *   - JSX children, spans, icons, null;
 *   - anything with a currency word or symbol already inside it.
 *
 * So the component either recognises a bare number and formats it completely,
 * or it does not touch the value at all. There is no half-formatted middle.
 */
import React from 'react';
import Riyal from './Riyal';
import { fmtMoney } from '../../utils/fmtMoney';

/**
 * A plain number and nothing else. Deliberately strict — no commas, no spaces,
 * no units, no second dot. Anything this rejects is rendered verbatim, so the
 * cost of being strict is an amount that stays as the caller wrote it, while
 * the cost of being loose is a mangled figure on a financial screen.
 */
const BARE_NUMBER = /^-?\d+(\.\d+)?$/;

/**
 * ONE implementation, in utils/fmtMoney — this component had grown its own copy
 * and a second implementation of "how money looks" is how two screens start
 * disagreeing about it.
 */
const formatAmount = fmtMoney;

const asAmount = (value, lang) => {
  const n = Number(value);
  return Number.isFinite(n) ? formatAmount(n, lang) : null;
};

/**
 * Format `children` only when it is certainly a bare number. `-0` is normalised
 * to `0.00`: a rounded-away negative rendering as "-0.00" reads as a credit
 * that is not there. `lang` is the language of the LINE the figure sits on
 * (see utils/fmtMoney): undefined/'ar' follows the company digit setting,
 * 'en' forces Latin — an English-only line never carries ١٢٣.
 */
const formatChild = (child, lang) => {
  if (typeof child === 'number') {
    return Number.isFinite(child) ? formatAmount(child, lang) : child;
  }
  if (typeof child === 'string' && BARE_NUMBER.test(child.trim())) {
    const n = Number(child.trim());
    return Number.isFinite(n) ? formatAmount(n, lang) : child;
  }
  return child;
};

export default function Money({ children, value, className = '', lang }) {
  const raw = children !== undefined && children !== null ? children : asAmount(value, lang);
  const content = children !== undefined && children !== null ? formatChild(raw, lang) : raw;
  return (
    <span dir="ltr" className={`inline-flex items-center gap-1 whitespace-nowrap ${className}`}>
      <Riyal />
      {content}
    </span>
  );
}
